import React, { useState } from 'react';
import { formatDate } from '../../lib/formatDate';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import {
  approveAccessRequest, approveProposal, rejectAccessRequest, rejectProposal,
} from './vendorsApi';
import {
  FIELD_LABEL, LEVEL_LABEL, PROPOSAL_KIND_LABEL, VENDOR_ERRORS, fieldValueLabel,
} from './vendorLabels';

const REASON_MAX = 500;

// A proposal's `changes` is `{field: {from, to}}`; tolerate a bare value too.
export function diffRows(changes) {
  return Object.entries(changes || {}).map(([key, entry]) => {
    const isPair = entry && typeof entry === 'object' && ('to' in entry || 'from' in entry);
    return {
      key,
      label: FIELD_LABEL[key] || entry?.label || key,
      from: isPair ? entry.from : undefined,
      to: isPair ? entry.to : entry,
      hasFrom: isPair && 'from' in entry,
    };
  });
}

// Approve / Reject with an inline required reason. `decide` resolves on success
// and rejects with the axios error; the card removes itself on success or on a
// 409 (someone else already handled it), and stays on anything else because
// nothing was saved.
function useDecision({ onGone, approve, reject, approvedToast }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const run = async (fn, okToast) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toastSuccess(okToast);
      onGone();
    } catch (err) {
      toastError(errorMessage(err, 'Something went wrong. Try again.', VENDOR_ERRORS));
      if (err?.response?.status === 409) onGone();
    } finally {
      setBusy(false);
    }
  };

  const confirmReject = () => {
    if (!reason.trim()) { setReasonError('A reason is required.'); return; }
    setReasonError('');
    run(() => reject(reason.trim()), 'Rejected.');
  };

  return {
    busy, rejecting, reason, reasonError,
    setReason, startReject: () => setRejecting(true), cancelReject: () => { setRejecting(false); setReasonError(''); },
    onApprove: () => run(approve, approvedToast),
    confirmReject,
  };
}

function Decision({ d, idPrefix }) {
  return (
    <>
      <div className="vd-card-actions">
        <button type="button" className="btn btn-primary btn-small" onClick={d.onApprove} disabled={d.busy}>Approve</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={d.startReject} disabled={d.busy}>Reject</button>
      </div>
      {d.rejecting && (
        <div className="vd-inline-reject">
          <div className="vd-form-row">
            <label htmlFor={`${idPrefix}-reason`}>Reason</label>
            <textarea id={`${idPrefix}-reason`} rows={2} maxLength={REASON_MAX} value={d.reason} onChange={(e) => d.setReason(e.target.value)} disabled={d.busy} />
            {d.reasonError && <p className="vd-field-error" role="alert">{d.reasonError}</p>}
          </div>
          <div className="vd-card-actions">
            <button type="button" className="btn btn-danger btn-small" onClick={d.confirmReject} disabled={d.busy}>Confirm reject</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={d.cancelReject} disabled={d.busy}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}

export function ProposalCard({ proposal, onGone }) {
  const d = useDecision({
    onGone,
    approve: () => approveProposal(proposal.id),
    reject: (reason) => rejectProposal(proposal.id, reason),
    approvedToast: 'Approved — saved to Odoo.',
  });
  const rows = diffRows(proposal.changes);
  const tagNames = proposal.tag_names || [];
  const title = proposal.kind === 'create'
    ? `New vendor: ${rows.find((r) => r.key === 'name')?.to ?? ''}`
    : proposal.vendor?.name;
  return (
    <li className="vd-card">
      <div className="vd-card-head">
        <strong>{title}</strong>
        <span className="vd-chip">{PROPOSAL_KIND_LABEL[proposal.kind] || proposal.kind}</span>
        <span className="vd-list-meta">{proposal.proposer_name || 'Someone'} · {formatDate(proposal.created_at)}</span>
      </div>
      {rows.length > 0 && (
        <ul className="vd-diff" aria-label="Proposed changes">
          {rows.map((r) => (
            <li key={r.key}>
              {r.label}:{' '}
              {r.hasFrom && <><span className="vd-from">{fieldValueLabel(r.key, r.from)}</span>{' → '}</>}
              <strong>{fieldValueLabel(r.key, r.to)}</strong>
            </li>
          ))}
        </ul>
      )}
      {tagNames.length > 0 && <p className="vd-list-meta">Specialities: {tagNames.join(', ')}</p>}
      {proposal.note && <p className="vd-comment-body">{proposal.note}</p>}
      <Decision d={d} idPrefix={`vd-p-${proposal.id}`} />
    </li>
  );
}

export function AccessRequestCard({ request, onGone }) {
  const d = useDecision({
    onGone,
    approve: () => approveAccessRequest(request.id),
    reject: (note) => rejectAccessRequest(request.id, note),
    approvedToast: 'Approved.',
  });
  return (
    <li className="vd-card">
      <div className="vd-card-head">
        <strong>{request.requester_name || request.user_name || 'Someone'}</strong>
        <span>wants</span>
        <span className="vd-chip">{LEVEL_LABEL[request.level] || request.level}</span>
        <span>on</span>
        <strong>{request.tag?.name || request.tag_name}</strong>
        <span className="vd-list-meta">{formatDate(request.created_at)}</span>
      </div>
      {request.reason && <p className="vd-comment-body">{request.reason}</p>}
      <Decision d={d} idPrefix={`vd-a-${request.id}`} />
    </li>
  );
}
